import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import type { Icon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { Waypoint } from 'src/app/shared/components/scroll';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

type InsightBoxParams = {
	topChannel: {
		channel: string;
		iconColor: string;
		signaturePercent: number;
		translationKey: TranslationKey;
		icon: React.FC<Icon>;
	};
	promoteLink: string;
	handlePromoteClick: () => void;
	handleShareClick: () => Promise<void>;
};

export const InsightBox: React.FC<InsightBoxParams> = ({
	topChannel,
	promoteLink,
	handlePromoteClick,
	handleShareClick,
}) => {
	const { typedTranslate } = useI18n();
	const trackOnce = useTrackOnce();
	const ChannelIcon = topChannel.icon;
	return (
		<>
			<Waypoint
				onEnter={async () =>
					trackOnce('petition_manage_signature_insights_box_view', { topChannel: topChannel.channel })
				}
			/>
			<div
				className="bg-background-overlay flex flex-col flex-nowrap items-center gap-6 rounded-lg p-6 text-center md:flex-row md:text-left"
				data-qa="signature-counts-insight-top-channel"
				data-testid="signature-counts-insight-message"
			>
				<ChannelIcon size={40} className={topChannel.iconColor} />
				{topChannel.channel === 'promotion' ? (
					<>
						<Text size="medium" fontWeight="light">
							<Translate value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.insights.promote" />
						</Text>
						<Button
							data-testid="signature-source-share-petition-button"
							data-qa="signature-source-share-petition-button"
							href={promoteLink}
							as="a"
							onClick={handlePromoteClick}
							variant="secondary-solid"
							size="x-small"
						>
							<Translate value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.promote_petition_button" />
						</Button>
					</>
				) : (
					<>
						<Text size="medium" fontWeight="light">
							<Translate
								value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.insights.share"
								replacements={{ channelName: typedTranslate(topChannel.translationKey) }}
							/>
						</Text>
						<Button
							className="w-full md:w-auto"
							data-testid="signature-source-share-petition-button"
							data-qa="signature-source-share-petition-button"
							variant="secondary-solid"
							size="x-small"
							onClick={handleShareClick}
						>
							<Translate value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.share_petition_button" />
						</Button>
					</>
				)}
			</div>
		</>
	);
};
